import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

/**
 * Cookie Consent Banner - GDPR Compliant
 * Displays consent notice for analytics, marketing, and functional cookies
 * Persists user preferences in localStorage for 365 days
 */
export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    // Check if user has already given consent
    const consentData = localStorage.getItem('rondanini_cookie_consent')
    if (!consentData) {
      // Show banner only if no prior consent
      setIsVisible(true)
    }
  }, [])

  const handleAcceptAll = () => {
    const consent = {
      analytics: true,
      marketing: true,
      functional: true,
      necessary: true,
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem('rondanini_cookie_consent', JSON.stringify(consent))
    setIsVisible(false)
    // Initialize analytics if available
    initializeAnalytics()
  }

  const handleRejectAll = () => {
    const consent = {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true, // Always required
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem('rondanini_cookie_consent', JSON.stringify(consent))
    setIsVisible(false)
  }

  const handleCustom = (settings: any) => {
    const consent = {
      ...settings,
      necessary: true, // Always required
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem('rondanini_cookie_consent', JSON.stringify(consent))
    setIsVisible(false)
    if (settings.analytics) {
      initializeAnalytics()
    }
  }

  const initializeAnalytics = () => {
    // Initialize Google Analytics, Mixpanel, or other analytics tools here
    console.log('Analytics initialized with user consent')
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t-2 border-navy-600 shadow-2xl"
        >
          <div className="max-w-6xl mx-auto px-4 py-6">
            {!showDetails ? (
              // Main Consent Banner
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    Cookie & Data Privacy
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    We use cookies to enhance your experience, measure performance, and deliver personalized content.
                    By clicking "Accept All", you consent to all cookie types. You can customize your preferences or
                    learn more by clicking "More Information".
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 whitespace-nowrap">
                  <button
                    onClick={handleRejectAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-4 py-2 text-sm font-medium text-navy-600 dark:text-navy-400 bg-navy-50 dark:bg-navy-900/30 hover:bg-navy-100 dark:hover:bg-navy-900/50 rounded-lg transition-colors"
                  >
                    More Information
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2 text-sm font-semibold text-white bg-navy-600 hover:bg-navy-700 rounded-lg transition-colors"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              // Detailed Settings
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                    Cookie Preferences
                  </h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Necessary Cookies (Always Enabled) */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        Necessary Cookies
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Essential for website functionality. These cookies are always enabled.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 w-4 h-4"
                    />
                  </div>
                  <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1 ml-4">
                    <li>• Session authentication and login</li>
                    <li>• Security and fraud prevention</li>
                    <li>• Form data and user preferences</li>
                    <li>• Shopping cart and checkout</li>
                  </ul>
                </div>

                {/* Analytics Cookies */}
                <CookieCategory
                  name="Analytics Cookies"
                  description="Help us understand how you use the site, what pages you visit, and how long you stay."
                  examples={[
                    'Google Analytics for traffic analysis',
                    'Page views and user behavior tracking',
                    'Performance metrics and user journeys',
                    'A/B testing and optimization'
                  ]}
                  defaultChecked={false}
                />

                {/* Functional Cookies */}
                <CookieCategory
                  name="Functional Cookies"
                  description="Enable personalized features and remember your preferences."
                  examples={[
                    'Dark mode preference',
                    'Language selection',
                    'Saved search and filter preferences',
                    'User activity and history'
                  ]}
                  defaultChecked={false}
                />

                {/* Marketing Cookies */}
                <CookieCategory
                  name="Marketing Cookies"
                  description="Used to display personalized advertisements and measure campaign effectiveness."
                  examples={[
                    'Facebook Pixel for ad retargeting',
                    'Google Ads conversion tracking',
                    'Email marketing campaign tracking',
                    'Third-party advertising networks'
                  ]}
                  defaultChecked={false}
                />

                {/* Privacy Policy Link */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    For more information, please see our{' '}
                    <a href="/privacy-policy" className="text-navy-600 dark:text-navy-400 hover:underline">
                      Privacy Policy
                    </a>
                    {' '}and{' '}
                    <a href="/terms-of-service" className="text-navy-600 dark:text-navy-400 hover:underline">
                      Terms of Service
                    </a>
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={() => {
                      handleCustom({
                        analytics: false,
                        functional: false,
                        marketing: false,
                      })
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-navy-600 dark:text-navy-400 bg-navy-50 dark:bg-navy-900/30 hover:bg-navy-100 dark:hover:bg-navy-900/50 rounded-lg transition-colors"
                  >
                    Save Preferences
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-6 py-2 text-sm font-semibold text-white bg-navy-600 hover:bg-navy-700 rounded-lg transition-colors"
                  >
                    Accept All
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Individual Cookie Category Component
 */
interface CategoryProps {
  name: string
  description: string
  examples: string[]
  defaultChecked: boolean
}

function CookieCategory({ name, description, examples, defaultChecked }: CategoryProps) {
  const [isChecked, setIsChecked] = useState(defaultChecked)

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 dark:text-white">
            {name}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {description}
          </p>
        </div>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
          className="mt-1 w-4 h-4 cursor-pointer"
        />
      </div>
      <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1 ml-4">
        {examples.map((example, idx) => (
          <li key={idx}>• {example}</li>
        ))}
      </ul>
    </div>
  )
}
